import { batch } from 'react-redux';
import { message } from 'antd';
import api from '@/api';
import { OBJECT_TYPE } from '@/components/MiningAndPumping/SummaryMode/constants';
import { roundByReference } from '@/shared/Chart/utils';
import { select } from '@/store';
import { normalizeData } from '@/utils';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

const ABORT_ERROR_CODE = 20;
let kyController = new AbortController();

const X_AXIS = {
    key: 'Period',
    title: 'Годы',
};

const DEFAULT_AXIS_PROPERTIES = {
    minBound: 0,
    maxBound: 0,
    majorTicksCount: 10,
    majorIntegerEnable: true,
    // FIXME: временно не используются
    // minorTicksCount: 10,
    // minorIntegerEnable: true,
};

const developmentIndicatorsModel = {
    name: 'developmentIndicators',
    state: {
        axes: null,
        activeAxes: null,
        data: null,
        useSummary: false,
        useFieldGroup: false,
        useError: false,
        loading: false,
        canceled: false,
    },
    reducers: {
        setAxes(state, payload) {
            return { ...state, axes: payload };
        },
        setActiveAxes(state, payload) {
            return {
                ...state,
                activeAxes: payload,
            };
        },
        setData(state, payload) {
            return {
                ...state,
                data: payload,
            };
        },
        setUseSummary(state, payload) {
            return {
                ...state,
                useSummary: payload,
            };
        },
        setUseError(state, payload) {
            return {
                ...state,
                useError: payload,
            };
        },
        setUseFieldGroup(state, payload) {
            return {
                ...state,
                useFieldGroup: payload,
            };
        },
        setLoading(state, payload) {
            return {
                ...state,
                loading: payload,
            };
        },
        setCanceled(state, payload) {
            return { ...state, canceled: payload };
        },
    },

    effects: dispatch => ({
        cancelRequest() {
            kyController.abort();
            kyController = new AbortController();

            batch(() => {
                this.setCanceled(true);
                this.setLoading(false);
            });
        },

        getRequestBody(_, rootState) {
            const { calculationObject } = rootState.tree;
            const { settingsSummary, version, init, monthPeriod } = rootState.summaryMode;
            const { useFieldGroup, useSummary, useError, loading } = rootState.developmentIndicators;
            const getWellsProperty = select(
                ({ lists: { activeGroup }, summaryMode: { activeStratums, activeFields } }) => ({
                    activeGroup,
                    activeStratums,
                    activeFields,
                }),
            );
            const { activeGroup, activeStratums: stratumIds, activeFields: fieldIds } = getWellsProperty(rootState);

            let body = null;

            if (
                (calculationObject?.id || activeGroup?.key) &&
                settingsSummary &&
                version &&
                stratumIds.length > 0 &&
                fieldIds.length > 0 &&
                !init &&
                !loading
            ) {
                body = {
                    versionId: version?.id,
                    monthPeriod,
                    useFieldGroup,
                    useSummary,
                    useError,
                    stratumIds,
                    fieldIds,
                };
                if (calculationObject?.node !== 'field') {
                    body[OBJECT_TYPE[calculationObject?.node]] = calculationObject?.id;
                }
                if (activeGroup?.children?.length) {
                    const wellIds = activeGroup?.children.map(well => well.id);
                    body.wellIds = wellIds;
                }
            }
            return body;
        },

        async fetchAxes() {
            try {
                const axesList = await api.miningAndPumping.fetchAxes();

                const axes = normalizeData(axesList, 'key').data;
                const activeAxes = axesList.reduce((acc, { key }) => ({ ...acc, [key]: true }), {});
                batch(() => {
                    this.setAxes(axes);
                    this.setActiveAxes(activeAxes);
                });
            } catch (error) {
                message.error('Не удалось получить данные для режима "Показатели разработки"');
            }
        },

        configureAxes(chartData, rootState) {
            const { axes } = rootState.developmentIndicators;
            const { minX, maxX, data } = chartData ?? {};
            const dateFormat = 'MM.YYYY';
            const xAxis = {
                ...X_AXIS,
                minBound: dayjs(minX, dateFormat).year(),
                maxBound: dayjs(maxX, dateFormat).year(),
            };
            let updatedAxes = { [xAxis.key]: xAxis, ...axes };
            const boundsByAxisKey = Object.values(data.charts).reduce((acc, indicator) => {
                const { y, axis: axisKey } = indicator;

                if (axes?.[axisKey]) {
                    const { minBound, maxBound } = (y ?? []).reduce(
                        (accY, yCoord) => {
                            accY.minBound = Math.min(accY.minBound, yCoord);
                            accY.maxBound = Math.max(accY.maxBound, yCoord);
                            return accY;
                        },
                        { minBound: Infinity, maxBound: -Infinity },
                    );

                    acc[axisKey] ||= { minBound: Infinity, maxBound: -Infinity };
                    acc[axisKey].minBound = Math.min(acc[axisKey].minBound, minBound);
                    acc[axisKey].maxBound = Math.max(acc[axisKey].maxBound, maxBound);
                }
                return acc;
            }, {});

            const {
                minBound: minBoundDefault,
                maxBound: maxBoundDefault,
                ...defaultAxisProperties
            } = DEFAULT_AXIS_PROPERTIES;

            updatedAxes = Object.values(updatedAxes ?? {}).reduce((acc, axis) => {
                let updatedAxis = axis;
                if (axis.key !== 'Period') {
                    let { minBound = minBoundDefault, maxBound = maxBoundDefault } = boundsByAxisKey[axis.key] ?? {};
                    [minBound, maxBound] = roundByReference(axis.key, [minBound, maxBound]);

                    updatedAxis = { ...axis, ...defaultAxisProperties, minBound, maxBound };
                }
                acc[axis.key] = updatedAxis;
                return acc;
            }, {});

            this.setAxes(updatedAxes);
        },

        /**
         * Очистка кэша и повторный запрос данных для Summary Mode
         */
        async refreshData() {
            const json = await this.getRequestBody();
            if (!json) return;

            try {
                const hideLoading = message.loading('Обновление данных...', 0);

                // Префлайт: если идёт расчёт — кэш не удаляем
                let preflight;
                try {
                    preflight = await api.miningAndPumping.fetchGraphDataSummary({ json, signal: kyController.signal });
                } catch (e) {
                    console.warn(encodeURIComponent)
                }
                const isCalculating = typeof preflight?.detail === 'string' && preflight.detail.length > 0;
                if (!isCalculating) {
                    // 1. Удаляем кэш
                    await api.miningAndPumping.deleteGraphDataSummaryCache({
                        json,
                        signal: kyController.signal
                    });
                }

                // 2. Поллинг повторного запроса данных до готовности
                const maxAttempts = 10;
                const delayMs = 2000;

                this.setLoading(true);
                let success = false;
                for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
                    try {
                        const data = await api.miningAndPumping.fetchGraphDataSummary({ json, signal: kyController.signal });
                        if (data && !data.detail) {
                            if (Object.keys(data) < 1) {
                                message.info('Недостаточно данных для построения графика в режиме "Показатели разработки"');
                            } else {
                                this.configureAxes(data);
                                this.setData(data);
                            }
                            success = true;
                            break;
                        }
                    } catch (e) {
                        console.warn(e)
                    }
                    await new Promise(resolve => setTimeout(resolve, delayMs));
                }

                hideLoading();
                this.setLoading(false);
                if (success) {
                    message.success('Данные успешно обновлены');
                } else {
                    message.warning('Данные ещё рассчитываются, попробуйте позже.');
                }
            } catch (error) {
                console.error('Error refreshing data:', error);
                this.setLoading(false);
                if (error.code !== ABORT_ERROR_CODE) {
                    message.error('Не удалось обновить данные');
                }
            }
        },

        async fetchData() {
            const json = await this.getRequestBody();
            if (!json) return;
            try {
                batch(() => {
                    this.setCanceled(false);
                    this.setLoading(true);
                    dispatch.approximation.setData(null);
                });

                const data = await api.miningAndPumping.fetchGraphDataSummary({ json, signal: kyController.signal });

                // Проверка на специальный ответ о расчете
                if (data?.detail && typeof data.detail === 'string') {
                    message.warning(data.detail);
                    this.setLoading(false);
                    return;
                }

                if (Object.keys(data) < 1) {
                    message.info('Недостаточно данных для построения графика в режиме "Показатели разработки"');
                } else {
                    this.configureAxes(data);
                    this.setData(data);
                }
            } catch (error) {
                console.error(error);
                if (error.code === ABORT_ERROR_CODE) return;
                this.setData(null);
                message.error('Не удалось получить данные для режима "Показатели разработки"');
            }
            this.setLoading(false);
        },
    }),

    selectors: (slice, createSelector) => ({
        filteredChartProps() {
            return createSelector(
                rootState => rootState.developmentIndicators.data,
                rootState => rootState.developmentIndicators.axes,
                rootState => rootState.developmentIndicators.activeAxes,
                rootState => rootState.summaryMode.summaryIndicators,

                (data, axes, activeAxes, indicators) => {
                    const { charts, date: xPoints } = data?.data ?? {};
                    return charts && xPoints && axes && activeAxes && indicators
                        ? Object.entries(charts).reduce(
                              (acc, [indicatorKey, indicatorData]) => {
                                  const { axis: axisKey } = indicatorData;

                                  if (activeAxes[axisKey] && indicators[indicatorKey]?.chartChecked) {
                                      const { y: yPoints } = indicatorData;
                                      const { title, lineStyle, typeDiagram, gradientColors, color } =
                                          indicators[indicatorKey];
                                      if (yPoints.some(p => p)) {
                                          let points = yPoints.map((yPoint, ix) => [
                                              xPoints[ix]?.length === 4 ? `01.${xPoints[ix]}` : xPoints[ix],
                                              yPoint,
                                          ]);

                                          if (typeDiagram === 'linear' || typeDiagram === 'area') {
                                              points = [points];
                                          }
                                          const dataset = {
                                              key: `chart${indicatorKey}`,
                                              entityKey: indicatorKey,
                                              title,
                                              lineStyle,
                                              typeDiagram,
                                              axis: axisKey,
                                              color,
                                              gradientColors,
                                              points,
                                          };
                                          acc.data.push(dataset);
                                          if (!acc.axes.some(({ key }) => key === axisKey))
                                              acc.axes.push(axes[axisKey]);
                                      }
                                  }
                                  return acc;
                              },
                              { data: [], axes: [] },
                          )
                        : { data: null, axes: null };
                },
            );
        },

        tableColumns() {
            return createSelector(
                rootState => rootState.summaryMode.summaryIndicators,
                summaryIndicators => {
                    return summaryIndicators
                        ? Object.keys(summaryIndicators).reduce(
                              (acc, key) => [
                                  ...acc,
                                  {
                                      headerName: summaryIndicators[key].title,
                                      field: key,
                                      hide: !summaryIndicators[key].tableChecked,
                                      valueFormatter: params => {
                                          return (
                                              params?.value?.toFixed(summaryIndicators?.[key]?.precision) ||
                                              params?.value
                                          );
                                      },
                                  },
                              ],
                              [{ field: 'date', headerName: 'Дата' }],
                          )
                        : [];
                },
            );
        },

        periodAxis() {
            return slice(({ axes }) => axes?.Period ?? null);
        },
    }),
};

export default developmentIndicatorsModel;
